import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";
import { money, parseApartmentUnits } from "../utils/rentalPortal";

export default function TenantAvailableApartments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = useState([]);
  const [location, setLocation] = useState("");

  useEffect(() => {
    const loadUnits = async () => {
      setLoading(true);
      try {
        const response = await api.get("/public/vacant-properties");
        setUnits(parseApartmentUnits(response.data));
      } catch (error) {
        console.error(error);
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };
    loadUnits();
  }, []);

  const filtered = useMemo(() => {
    return units.filter((unit) => {
      const passLocation =
        !location.trim() || unit.location.toLowerCase().includes(location.trim().toLowerCase());
      return passLocation;
    });
  }, [location, units]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/92" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <div className="relative z-10 px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 rounded-xl border border-white/10 bg-slate-950/90 p-6 shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-300">Tenant Units</p>
          <h1 className="mt-2 text-3xl font-bold text-white">Available Apartments</h1>
          <p className="mt-2 text-sm text-slate-300">
            Browse current vacant units, view details, and apply to rent.
          </p>
        </header>

        <section className="mb-5 grid gap-3 rounded-xl border border-white/10 bg-slate-950/90 p-4 shadow-2xl md:grid-cols-[1fr_160px]">
          <input
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            placeholder="Filter by location"
            className="rounded-lg border border-white/10 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
          />
          <button
            type="button"
            onClick={() => {
              setLocation("");
            }}
            className="rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
          >
            Clear
          </button>
        </section>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300 shadow-2xl">
            Loading apartment units...
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/20 bg-slate-950/90 py-10 text-center text-slate-300 shadow-2xl">
            No available apartments found.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((unit) => (
              <article
                key={unit.id}
                className="rounded-xl border border-white/10 bg-slate-950/92 p-5 shadow-2xl"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal-300">
                  {unit.availability_status}
                </p>
                <h2 className="mt-2 text-2xl font-bold text-white">{unit.apartment_name}</h2>
                <p className="mt-1 text-sm text-slate-300">{unit.location}</p>
                <p className="mt-3 text-xl font-bold text-cyan-200">{money(unit.monthly_rent)}</p>
                <p className="mt-3 min-h-[60px] text-sm leading-6 text-slate-300">{unit.description}</p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Link
                    to={`/tenant/apartments/${unit.id}`}
                    className="rounded-lg border border-white/15 px-3 py-2 text-center text-sm font-semibold text-slate-100 transition hover:bg-white/10"
                  >
                    View Details
                  </Link>
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/apply/${unit.id}`)}
                    className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                  >
                    Apply to Rent
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
